import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import {
  ServiceLine,
  ServiceLineType,
  OverageData,
  CreateServiceLineRequest,
  DataUsage,
} from '@/shared/types/serviceLineTypes';
import { Product } from '@/shared/types/productTypes';
import { PaginatedResponse, SingleResponse } from '@/shared/types/responseTypes';

/** Infinite scroll fetch for dashboard-style load-more. */
export const useInfiniteAccountServiceLines = (searchQuery: string | undefined) => {
  return useInfiniteQuery({
    queryKey: ['service-lines-infinite', searchQuery],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<ServiceLine>>(
        `/api/v1/service-lines`,
        {
          params: {
            page: pageParam,
            includeDataUsage: true,
            includeActiveAlerts: true,
            searchQuery
          }
        }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
  });
};

// Get Service Line
export const useServiceLine = (serviceLineNumber: string) => {
  return useQuery({
    queryKey: ['service-line', serviceLineNumber],
    queryFn: async () => {
      const response = await apiClient.get<SingleResponse<ServiceLine>>(
        `/api/v1/service-lines/${serviceLineNumber}`,
        { params: { includeDataUsage: true } }
      );
      return response.data;
    },
    enabled: !!serviceLineNumber
  });
};

// Create Service Line Mutation
export const useCreateServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (createServiceLineRequest: CreateServiceLineRequest) => {
      const response = await apiClient.post<SingleResponse<ServiceLine>>(
        `/api/v1/service-lines`,
        {
          body: createServiceLineRequest,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-line-create'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Add Terminal to Service Line Mutation
export const useAddTerminalToServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, terminalId }: { serviceLineNumber: string, terminalId: string }) => {
      return await apiClient.post<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/user-terminals/${terminalId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-add-terminal', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove Terminal from Service Line Mutation
export const useRemoveTerminalFromServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, terminalId }: { serviceLineNumber: string, terminalId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/user-terminals/${terminalId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-remove-terminal', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Add Customer to Service Line Mutation
export const useAddCustomerToServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, customerId }: { serviceLineNumber: string, customerId: string }) => {
      return await apiClient.post<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/customers/${customerId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-add-customer', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove Customer from Service Line Mutation
export const useRemoveCustomerFromServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, customerId }: { serviceLineNumber: string, customerId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/customers/${customerId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-remove-customer', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Rename Service Line Mutation
export const useRenameServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, serviceLineName }: { serviceLineNumber: string, serviceLineName: string }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/service-line-name`,
        {
          body: { serviceLineName },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-rename-name', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Get Service Line Available Subscription Plans Query
export const useServiceLineAvailableSubscriptionPlans = (serviceLineNumber: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ['service-line-available-subscriptions', serviceLineNumber],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<Product>>(
        `/api/v1/service-lines/${serviceLineNumber}/available-subscriptions`
      );
      return response.data;
    },
    enabled: !!serviceLineNumber && enabled
  });
};

// Update Service Line Subscription Plan Mutation
export const useUpdateServiceLineSubscriptionPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, orbiusProductReferenceId }: { serviceLineNumber: string, orbiusProductReferenceId: string }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/products/${orbiusProductReferenceId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-update-subscription-plan', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Deactivate Service Line Mutation
export const useDeactivateServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber }: { serviceLineNumber: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-deactivate', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Pause Service Line Mutation
export const usePauseServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, resumesAt }: { serviceLineNumber: string, resumesAt?: string | null }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/pause`,
        {
          body: { resumesAt: resumesAt ? resumesAt : undefined },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-pause', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Resume Service Line Mutation
export const useResumeServiceLine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber }: { serviceLineNumber: string }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/resume`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-resume', variables.serviceLineNumber] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Get Service Line Usage Query
// If month(yyyy-MM) is not provided, it will return the usage summary for last 12 months
export const useServiceLineUsage = (serviceLineNumber: string | undefined, month: string | undefined) => {
  return useQuery({
    queryKey: ['service-line-usage-summary', serviceLineNumber, month],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<DataUsage>>(
        `/api/v1/service-lines/${serviceLineNumber}/usage-summary`,
        { params: { month: month ? month : undefined } }
      );
      return response.data.results || [];
    },
    enabled: !!serviceLineNumber
  });
};

// Create Service Line Overage Mutation
export const useCreateServiceLineOverage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceLineNumber, overageData }: { serviceLineNumber: string, overageData: OverageData }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/overage`,
        {
          body: overageData,
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-create-overage', variables.serviceLineNumber] });
    },
  });
};

// Remove Service Line Overage Mutation
export const useRemoveServiceLineOverage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber }: { serviceLineNumber: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/overage`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-remove-overage', variables.serviceLineNumber] });
    },
  });
};

// Update Public IP Mutation
export const useUpdatePublicIp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, publicIp }: { serviceLineNumber: string, publicIp: boolean }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/public-ip`,
        {
          body: { publicIp },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-update-public-ip', variables.serviceLineNumber] });
    },
  });
};

// Update Service Line Type Mutation
export const useUpdateServiceLineType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, type }: { serviceLineNumber: string, type: ServiceLineType }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/type`,
        {
          body: { type },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-update-type', variables.serviceLineNumber] });
    },
  });
};

// Update Service Line Notes Mutation
export const useUpdateServiceLineNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceLineNumber, notes }: { serviceLineNumber: string, notes: string }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/service-lines/${serviceLineNumber}/notes`,
        {
          body: { notes },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-line-update-notes', variables.serviceLineNumber] });
    },
  });
};